import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { makeStyles, Grid, Paper, Theme } from "@material-ui/core";
import ReactMarkdown from "react-markdown";
import gfm from "remark-gfm";
import FeatherIcon from "feather-icons-react";
import { KTextInput, KButton, KList } from "@kyburz/kdesign-system";
import {
  getEmailTemplates,
  updateEmailTemplates,
} from "../../../apiCalls/settings/emailTemplates";
import useSetState from "../../../hooks/useSetState";
import { humanize } from "../../../utils/utils";
import { setAlert } from "../../../redux/actions/actions";

const useStyles = makeStyles((theme: Theme) => ({
  grid: {
    height: "90vh",
  },
  header: {
    fontSize: 20,
    fontWeight: 500,
    color: theme.palette.common.black,
    width: "100%",
    paddingBottom: 16,
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
  },
  headerMarkdown: {
    fontSize: 20,
    fontWeight: 500,
    color: theme.palette.common.white,
  },
  paper: {
    overflow: "scroll",
    padding: 32,
    boxShadow: "none",
    height: "85vh",
  },
  paperDarkMode: {
    overflow: "scroll",
    background: theme.palette.grey[800],
    position: "relative",
    bottom: "85vh",
    padding: 32,
    height: "85vh",
    color: theme.palette.common.white,
    boxShadow: "0px -6px 27px -3px rgba(0,0,0,0.41)",
  },
  paperDarkModeDisappear: {
    background: theme.palette.grey[800],
    padding: 32,
    display: "none",
    color: theme.palette.common.white,
    boxShadow: "0px -6px 27px -3px rgba(0,0,0,0.41)",
  },
  flex: {
    display: "flex",
  },
  flexMargin: {
    display: "flex",
    marginBottom: 16,
  },
  icon: {
    alignSelf: "center",
    marginLeft: 8,
    cursor: "pointer",
  },
  buttons: {
    float: "right",
    position: "absolute",
    bottom: 32,
    right: 32,
  },
}));

const EmailTemplates: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [
    { markdown, devMode, data, templates, selectedTemplate, templateId },
    setState,
  ] = useSetState({
    markdown: "",
    devMode: false,
    data: {},
    templates: [],
    selectedTemplate: "",
    templateId: "",
  });

  const handleTextInputChange = (e): void => {
    setState({ [e.target.name]: e.target.value });
  };

  const handleMarkdownToggle = (): void => {
    setState({ devMode: !devMode });
  };

  const handleRowClick = (index: number): void => {
    setState({
      selectedTemplate: templates[index],
      markdown: templates[index][1]?.description || "",
      templateId: selectedTemplate[1]?.template_id,
    });
  };

  const handleUpdateEmailTemplates = (name: string, value: string) => {
    data[selectedTemplate[0]][name] = value;
    updateEmailTemplates(data).then((res: any) => {
      if (res.data) {
        dispatch(setAlert("Template update successful!", "success"));
        setState({ devMode: false });
      } else {
        dispatch(setAlert("Error updating template", "error"));
      }
    });
  };

  useEffect(() => {
    getEmailTemplates().then((res: any) => {
      const mappedTemplates = Object.entries(res.data);
      // eslint-disable-next-line @typescript-eslint/no-shadow
      const { data } = res;
      setState({
        data,
        templates: mappedTemplates.filter(
          (x) => x[0] !== "tool" && x[0] !== "_id" && x[0] !== "report_send",
        ),
      });
    });
  }, [setState]);

  useEffect(() => {
    if (templates.length) {
      setState({
        selectedTemplate: templates[0],
        markdown: templates[0][1]?.description,
      });
    }
  }, [templates, setState]);

  useEffect(() => {
    if (selectedTemplate[1]) {
      setState({ templateId: selectedTemplate[1]?.template_id });
    }
  }, [selectedTemplate, setState]);

  return (
    <Grid container spacing={2}>
      <Grid item sm={6} className={classes.grid}>
        <Paper className={classes.paper}>
          <div className={classes.header}>Email Templates</div>
          <KList
            preSelected={0}
            elements={templates.map((x) => humanize(x[0]))}
            onRowClick={(index: number) => handleRowClick(index)}
            width="auto"
            height={600}
            endIcon={{ icon: "code", onClick: handleMarkdownToggle }}
          />
        </Paper>
        <Paper
          className={
            devMode ? classes.paperDarkMode : classes.paperDarkModeDisappear
          }
        >
          <div className={classes.flex}>
            <div className={classes.headerMarkdown}>Markdown</div>
            <div
              onClick={() => window.open("https://commonmark.org/help/")}
              className={classes.icon}
            >
              <FeatherIcon icon="info" size={16} />
            </div>
          </div>
          <KTextInput
            name="markdown"
            value={markdown}
            onChange={handleTextInputChange}
            multiline
            width="100%"
            rows={28}
            rowsMax={28}
            transparent
          />
          <div className={classes.buttons}>
            <KButton
              children="Cancel"
              theme="secondary"
              onClick={handleMarkdownToggle}
              style={{ marginRight: 8 }}
            />
            <KButton
              children="Save"
              onClick={() =>
                handleUpdateEmailTemplates("description", markdown)
              }
            />
          </div>
        </Paper>
      </Grid>
      <Grid item sm={6}>
        <Paper className={classes.paper}>
          <div className={classes.header}>{humanize(selectedTemplate[0])}</div>
          <div className={classes.flexMargin}>
            <KTextInput
              label="Template ID"
              value={templateId}
              name="templateId"
              onChange={handleTextInputChange}
              width="100%"
              style={{ width: "100%" }}
            />
            <KButton
              children="Update"
              onClick={() =>
                handleUpdateEmailTemplates("template_id", templateId)
              }
              style={{ marginTop: 40, marginLeft: 8 }}
            />
          </div>
          <div className={classes.header}>Description</div>
          <ReactMarkdown plugins={[gfm]}>{markdown}</ReactMarkdown>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default EmailTemplates;
